// v18 C3: één datumstijl voor datums zonder tijd: "di 13 okt", met jaar alleen als het een ander jaar is ("di 13 okt 2025").
// Staat hier (zonder imports) zodat dit bestand op zichzelf draait; lib/datum.js exporteert hem door.
const DAG_KORT = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const MAAND_KORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const NL = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Amsterdam", year: "numeric", month: "2-digit", day: "2-digit" });
// v18-R1 R9: via formatToParts, zodat een afwijkende en-CA-notatie de datum niet kan breken.
const nlDelen = (d) => {
  const p = Object.fromEntries(NL.formatToParts(d).map((x) => [x.type, x.value]));
  return [Number(p.year), Number(p.month), Number(p.day)];
};

function delen(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  const nl = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v || "");
  if (nl) return [Number(nl[3]), Number(nl[2]), Number(nl[1])];
  const d = v ? new Date(v) : null;
  if (!d || isNaN(d)) return null;
  return nlDelen(d);   // tijdstempel → de NL-kalenderdag
}

export function datumKort(v, nu = new Date()) {
  const p = delen(v);
  if (!p || !MAAND_KORT[p[1] - 1]) return "";
  const [y, m, d] = p;
  const dag = DAG_KORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const ditJaar = nlDelen(nu)[0];
  return `${dag} ${d} ${MAAND_KORT[m - 1]}${y !== ditJaar ? ` ${y}` : ""}`;
}

export const NONE_KEY = "__none__";
export const GEEN_OPVOLGER = "__geen__";
export const INTERN_KEY = "__intern__";
export const isIntern = (a) => a?.soort === "intern";
// "Eigenaar: Sam · met Milos" voor een interne afspraak.
export function internLabel(a) {
  const c = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const anderen = (a.betrokkenen || []).filter((b) => b !== a.eigenaarPersoon).map(c);
  // v20 C: de locatie is alleen een label
  return `Eigenaar: ${c(a.eigenaarPersoon) || "—"}${anderen.length ? ` · met ${anderen.join(", ")}` : ""}${a.locatie ? ` · over ${a.locatie}` : ""}`;
}
export const EIGENAAR_LABEL = { bedrijfsleider: "Bedrijfsleider", om: "OM", beide: "Beide", afdeling: "Afdeling" };
export const EIGENAAR_ORDER = { bedrijfsleider: 0, om: 1, beide: 2, afdeling: 3 };
// v19-N3: eigenaar- en afdelingsfilter samen; "alle" = alles. Een gekozen afdeling houdt alleen die afdeling over.
export const pastEigenaarAfdeling = (a, eigenaar = "alle", afdeling = "alle") =>
  (eigenaar === "alle" || a.eigenaar === eigenaar) && (afdeling === "alle" || a.afdeling === afdeling);

export const SORTS = [
  { key: "deadline", label: "Deadline (te laat eerst)" },
  { key: "locatie", label: "Locatie (A→Z)" },
  { key: "aangemaakt", label: "Ingepland (nieuwste eerst)" },
  { key: "bedrijfsleider", label: "Bedrijfsleider" },
  { key: "eigenaar", label: "Eigenaar" },
];

export const GROEPEN = [
  { key: "telaat", label: "Te laat", dot: "bg-rose-500", text: "text-rose-600", border: "border-rose-200", tint: "bg-rose-50", top: "border-t-rose-500" },
  { key: "dezeweek", label: "Deze week", dot: "bg-amber-500", text: "text-amber-600", border: "border-amber-200", tint: "bg-amber-50", top: "border-t-amber-500" },
  { key: "later", label: "Later", dot: "bg-blue-600", text: "text-blue-600", border: "border-blue-200", tint: "bg-blue-50", top: "border-t-blue-600" },
  { key: "geen", label: "Zonder deadline", dot: "bg-zinc-400", text: "text-zinc-500", border: "border-zinc-200", tint: "bg-zinc-50", top: "border-t-zinc-400" },
];
export const GROEP = Object.fromEntries(GROEPEN.map((g) => [g.key, g]));

export function effDeadline(a) { return a.effectiveDeadline || a.deadline || null; }

function midnight(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Korte NL-datum ("10 okt") uit "YYYY-MM-DD"; leeg/ongeldig → "".
export function kortDatum(iso) {
  return datumKort(iso);   // v18 C3
}

// NL-datum DD-MM-YYYY uit een ISO-timestamp/datum; lege/ongeldige invoer → "".
export function fmtDateNL(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function fmtDateTimeNL(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d)) return "";
  return d.toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function deadlineBucket(a) {
  // Gesloten afspraken (nagekomen/niet) kunnen nooit "te laat" zijn (zelfde semantiek als a.overdue).
  if (a.status && a.status !== "open") return "afgerond";
  const dl = effDeadline(a);
  if (!dl) return "geen";
  const d = new Date(dl + "T00:00:00");
  if (isNaN(d)) return "geen";
  const today = midnight(new Date());
  const diff = Math.round((d - today) / 86400000);
  // Alleen echt te laat (a.overdue) hoort in "telaat"; alle peilmomenten afgevinkt = overdue=false.
  if (diff < 0 && a.overdue) return "telaat";
  if (diff < 0) return "later";
  const dow = today.getDay() || 7;
  const endOfWeek = midnight(today); endOfWeek.setDate(today.getDate() + (7 - dow));
  if (d <= endOfWeek) return "dezeweek";
  const in28 = midnight(today); in28.setDate(today.getDate() + 28);
  if (d <= in28) return "volgende";
  return "later";
}

// De vier groepen van teller/bord/stip: hergebruikt deadlineBucket (geen tweede indeling).
export function groepVan(a) {
  const b = deadlineBucket(a);
  if (b === "volgende") return "later";
  if (b === "afgerond") return "geen";
  return b;
}

export function relLabel(a) {
  const dl = effDeadline(a);
  if (!dl) return "Geen deadline";
  const d = new Date(dl + "T00:00:00");
  if (isNaN(d)) return "";
  const today = midnight(new Date());
  const diff = Math.round((d - today) / 86400000);
  if ((a.status && a.status !== "open") || !a.overdue) {
    if (diff === 0) return "Vandaag";
    if (diff === 1) return "Morgen";
    if (diff > 0) return `Over ${diff} dagen`;
    return datumKort(dl);
  }
  if (diff < 0) { const n = Math.abs(diff); return `${n} dag${n > 1 ? "en" : ""} te laat`; }
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  return `Over ${diff} dagen`;
}

export function compareRows(sortBy) {
  return (x, y) => {
    if (sortBy === "locatie") {
      const c = (x.locatie || "\uffff").localeCompare(y.locatie || "\uffff", "nl");
      if (c) return c;
    }
    // v18 C2: "Klaar om af te ronden" bovenaan (één klik werk), onder elke sortering.
    if (!!x.klaarOmAfTeRonden !== !!y.klaarOmAfTeRonden) return x.klaarOmAfTeRonden ? -1 : 1;
    if (sortBy === "locatie") {
      // al vergeleken
    } else if (sortBy === "aangemaakt") {
      const c = (y.timestamp || "").localeCompare(x.timestamp || "");
      if (c) return c;
    } else if (sortBy === "bedrijfsleider") {
      const c = (x.bedrijfsleider || "\uffff").toLowerCase().localeCompare((y.bedrijfsleider || "\uffff").toLowerCase(), "nl");
      if (c) return c;
    } else if (sortBy === "eigenaar") {
      const c = (EIGENAAR_ORDER[x.eigenaar] ?? 9) - (EIGENAAR_ORDER[y.eigenaar] ?? 9);
      if (c) return c;
    }
    if (!!x.overdue !== !!y.overdue) return x.overdue ? -1 : 1;
    return (effDeadline(x) || "9999-12-31").localeCompare(effDeadline(y) || "9999-12-31");
  };
}

export function sortAfgerond(x, y) {
  return (y.afgerondOp || y.timestamp || "").localeCompare(x.afgerondOp || x.timestamp || "");
}

// v16 A: buckets, Intern en "Overig" blijven achteraan; bij "Locatie (A→Z)" verder strikt op naam.
// v19-A-R R9: zonder locatie, met een afdeling als eigenaar → eigen groep "Afdeling: <naam>" naast Hoofdkantoor.
export const AFD_PREFIX = "__afd__:";
const groepRang = (k, buckets) => (k === NONE_KEY ? 3 : k === INTERN_KEY ? 2 : buckets.includes(k) || k.startsWith(AFD_PREFIX) ? 1 : 0);
export const groepSleutel = (a) => (isIntern(a) ? INTERN_KEY : a.locatie ? a.locatie
  : a.eigenaar === "afdeling" && a.afdeling ? `${AFD_PREFIX}${a.afdeling}` : NONE_KEY);
export const groepLabel = (key, items = []) => (key === NONE_KEY ? "Overig / geen locatie" : key === INTERN_KEY ? "Intern"
  : key.startsWith(AFD_PREFIX) ? `Afdeling: ${items[0]?.afdelingNaam || key.slice(AFD_PREFIX.length)}` : key);
export function compareGroepen(sortBy, buckets = []) {
  return (a, b) => (groepRang(a.key, buckets) - groepRang(b.key, buckets))
    || (sortBy === "locatie" ? 0 : ((b.telaat > 0) - (a.telaat > 0)))
    || a.label.localeCompare(b.label, "nl");
}
// Afgerond/Verwijderd: bij "Locatie (A→Z)" op locatie, daarbinnen de bestaande volgorde (stabiele sort).
export function sorteerAfgerondLijst(items, { sortBy, buckets = [], verwijderd = false }) {
  const basis = verwijderd ? [...items] : [...items].sort(sortAfgerond);
  if (sortBy !== "locatie") return basis;
  const cmp = compareGroepen("locatie", buckets);
  const g = (a) => ({ key: groepSleutel(a), label: a.locatie || (groepSleutel(a).startsWith(AFD_PREFIX) ? groepLabel(groepSleutel(a), [a]) : ""), telaat: 0 });
  return basis.sort((x, y) => cmp(g(x), g(y)));
}

export function cpVoortgang(a) {
  const cps = a.checkpoints || [];
  return { done: cps.filter((c) => c.done).length, total: cps.length };
}

export function isGemeld(a) {
  return Object.values(a.blReported || {}).some((r) => r && (r.reported || (r.note || "").trim()));
}

// Compacte weergave van vrije tekst: regels achter elkaar met " · ", lijsttekens ervoor weg.
export function eenRegel(t) {
  return String(t || "").split("\n").map((r) => r.replace(/^\s*[-•*]\s+/, "").trim()).filter(Boolean).join(" · ");
}

// "vr 2 okt" uit yyyy-MM-dd; ongeldig → de invoer zelf.
export function cpDag(ds) {
  return datumKort(ds) || ds || "";
}

export function plural(n, een, meer) { return `${n} ${n === 1 ? een : meer}`; }

export function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

const AVATAR_PALETTE = ["bg-indigo-600", "bg-pink-600", "bg-cyan-600", "bg-violet-600", "bg-teal-600", "bg-orange-600"];
export function avatarKleur(name) {
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// v18-R1 R5: opvolger-chips = alleen actieve mensen (OM's + oversight-eigenaar van een interne afspraak); de ingelogde OM vooraan.
export function opvolgerChips({ oms = [], afspraken = [], actief = [], ik = "" }) {
  const act = new Set(actief);
  const extra = afspraken.filter(isIntern).map((a) => a.eigenaarPersoon).filter((o) => o && !oms.includes(o));
  const lijst = [...oms, ...[...new Set(extra)].sort()].filter((o) => act.has(o));
  return lijst.includes(ik) ? [ik, ...lijst.filter((o) => o !== ik)] : lijst;
}

// Valt een afspraak onder het opvolgerfilter? "Geen" = geen ACTIEVE opvolger (v8).
export function pastOpvolger(a, filter) {
  if (filter === "alle") return true;
  if (filter === GEEN_OPVOLGER) return !(a.opvolgerEffectief && a.opvolgerActief !== false);
  return a.opvolgerEffectief === filter;
}

// v18-R1 R2: alleen een klik op een opvolgerchip telt als keuze; anders opent een OM op zijn eigen afspraken.
export function startOpvolger(prefs, user) {
  if (prefs && prefs.opvolgerGekozen === true && prefs.opvolger) return prefs.opvolger;
  return user.role === "om" ? user.username : "alle";
}
